/**
 * Session Validation Utilities
 *
 * Helpers to validate and diagnose session generation issues.
 *
 * Moved from src/utils/sessionValidation.ts in R31 (DDD Fase 4).
 */

import { type ModuleId, getFamiliesForModule, getAllowedTiersForGroup, getTierRangeForGroup, QUESTIONS_BY_FAMILY } from '../../content';
import { GroupId } from '../../config/systemConfig';
import { debug, warn, error as logError, group, groupEnd } from '../../utils/logger';
export interface SessionValidationReport {
  moduleId: ModuleId;
  group: GroupId;
  isValid: boolean;
  errors: string[];
  warnings: string[];
  stats: {
    totalFamilies: number;
    eligibleFamilies: number;
    familiesWithQuestions: number;
    totalQuestionsAvailable: number;
    tierRange: {
      min: number;
      max: number;
    };
  };
  familyDetails: {
    familyId: string;
    tier: number;
    isEligible: boolean;
    questionCount: number;
    questionsInTier: number;
  }[];
}

/**
 * Validate if a module can generate a session for a given group.
 * Returns a detailed report for debugging.
 */
export function validateSessionAvailability(moduleId: ModuleId, group: GroupId): SessionValidationReport {
  const report: SessionValidationReport = {
    moduleId,
    group,
    isValid: false,
    errors: [],
    warnings: [],
    stats: {
      totalFamilies: 0,
      eligibleFamilies: 0,
      familiesWithQuestions: 0,
      totalQuestionsAvailable: 0,
      tierRange: {
        min: 0,
        max: 0
      }
    },
    familyDetails: []
  };

  // Get module families
  const allFamilies = getFamiliesForModule(moduleId);
  report.stats.totalFamilies = allFamilies.length;
  if (allFamilies.length === 0) {
    report.errors.push(`No families found in taxonomy for module ${moduleId}`);
    return report;
  }

  // Get allowed tiers for group
  const allowedTiers = getAllowedTiersForGroup(group);
  const tierRange = getTierRangeForGroup(group);
  report.stats.tierRange = tierRange;

  // Filter eligible families
  const eligibleFamilies = allFamilies.filter(f => allowedTiers.has(f.tier));
  report.stats.eligibleFamilies = eligibleFamilies.length;
  if (eligibleFamilies.length === 0) {
    report.errors.push(`No families within allowed tiers [${tierRange.min}-${tierRange.max}] for group ${group}`);
    report.warnings.push(`Module has ${allFamilies.length} families, but none match the group's tier range`);
    return report;
  }

  // Check question availability for each family
  allFamilies.forEach(family => {
    const isEligible = allowedTiers.has(family.tier);
    const familyQuestions = QUESTIONS_BY_FAMILY.get(family.familyId) || [];
    const questionsInTier = familyQuestions.filter(q => allowedTiers.has(q.tier)).length;
    report.familyDetails.push({
      familyId: family.familyId,
      tier: family.tier,
      isEligible,
      questionCount: familyQuestions.length,
      questionsInTier
    });
    if (isEligible && questionsInTier > 0) {
      report.stats.familiesWithQuestions++;
      report.stats.totalQuestionsAvailable += questionsInTier;
    }
  });

  // Validation
  if (report.stats.familiesWithQuestions === 0) {
    report.errors.push(`No eligible families have questions within allowed tiers [${tierRange.min}-${tierRange.max}]`);
    report.warnings.push(`This indicates missing question content for this module/group combination`);
  } else if (report.stats.totalQuestionsAvailable < 10) {
    report.warnings.push(`Only ${report.stats.totalQuestionsAvailable} questions available (recommended: 10+)`);
  }
  report.isValid = report.errors.length === 0 && report.stats.familiesWithQuestions > 0;
  return report;
}

/**
 * Log a validation report to console (for debugging).
 */
export function logValidationReport(report: SessionValidationReport): void {
  group(`📊 Session Validation: ${report.moduleId} (Group ${report.group})`);
  debug(`Status: ${report.isValid ? '✅ Valid' : '❌ Invalid'}`);
  if (report.errors.length > 0) {
    group('❌ Errors:');
    report.errors.forEach(e => logError(`  ${e}`));
    groupEnd();
  }
  if (report.warnings.length > 0) {
    group('⚠️ Warnings:');
    report.warnings.forEach(w => warn(`  ${w}`));
    groupEnd();
  }
  group('📈 Statistics:');
  debug(`  Total families: ${report.stats.totalFamilies}`);
  debug(`  Eligible families: ${report.stats.eligibleFamilies}`);
  debug(`  Families with questions: ${report.stats.familiesWithQuestions}`);
  debug(`  Total questions available: ${report.stats.totalQuestionsAvailable}`);
  debug(`  Tier range: [${report.stats.tierRange.min}-${report.stats.tierRange.max}]`);
  groupEnd();
  if (report.familyDetails.length > 0 && !report.isValid) {
    group('🔍 Family Details:');
    report.familyDetails.forEach(fd => {
      const status = fd.isEligible ? fd.questionsInTier > 0 ? '✅' : '⚠️ No questions' : '❌ Out of tier range';
      debug(`  ${status} ${fd.familyId} (tier ${fd.tier}): ${fd.questionsInTier}/${fd.questionCount} questions available`);
    });
    groupEnd();
  }
  groupEnd();
}

/**
 * Quick check if a module has content for a group (for UI).
 */
export function hasContentForGroup(moduleId: ModuleId, group: GroupId): boolean {
  const report = validateSessionAvailability(moduleId, group);
  return report.isValid;
}

// ─── §6.3 Session Integrity Checking ──────────────────────────────────────────

/**
 * Lightweight checksum for session state integrity.
 * Detects mid-session corruption (e.g. localStorage race conditions).
 */
export interface SessionIntegritySnapshot {
  /** Hash of question IDs in order */
  questionHash: number;
  /** Number of answers recorded */
  answerCount: number;
  /** Timestamp of snapshot */
  timestamp: number;
  /** Module being practiced */
  moduleId: ModuleId;
  /** Group being practiced */
  group: GroupId;
}

/**
 * Compute a simple numeric hash for an array of strings.
 * Not cryptographic — just for integrity detection.
 */
function simpleHash(strings: string[]): number {
  let hash = 0;
  const combined = strings.join('|');
  for (let i = 0; i < combined.length; i++) {
    const char = combined.charCodeAt(i);
    hash = (hash << 5) - hash + char | 0; // Convert to 32-bit integer
  }
  return hash;
}

/**
 * Create an integrity snapshot of the current session state.
 */
export function createSessionSnapshot(questionIds: string[], answerCount: number, moduleId: ModuleId, group: GroupId): SessionIntegritySnapshot {
  return {
    questionHash: simpleHash(questionIds),
    answerCount,
    timestamp: Date.now(),
    moduleId,
    group
  };
}

/**
 * Validate current session state against a previous snapshot.
 * Returns null if valid, or an error message if corruption detected.
 */
export function validateSessionIntegrity(currentQuestionIds: string[], currentAnswerCount: number, currentModuleId: ModuleId, currentGroup: GroupId, snapshot: SessionIntegritySnapshot): string | null {
  const currentHash = simpleHash(currentQuestionIds);
  if (currentHash !== snapshot.questionHash) {
    return `Session corruption: question list changed mid-session (expected hash ${snapshot.questionHash}, got ${currentHash})`;
  }
  if (currentAnswerCount < snapshot.answerCount) {
    return `Session corruption: answer count decreased (was ${snapshot.answerCount}, now ${currentAnswerCount})`;
  }
  if (currentModuleId !== snapshot.moduleId) {
    return `Session corruption: module changed mid-session (was ${snapshot.moduleId}, now ${currentModuleId})`;
  }
  if (currentGroup !== snapshot.group) {
    return `Session corruption: group changed mid-session (was ${snapshot.group}, now ${currentGroup})`;
  }
  return null; // Valid
}